import { useState, useEffect, useRef } from 'react';
import { useLlmProviders, useTestProvider } from '../hooks/useLlm';
import { useSettings, useSaveSettings, useUploadLogo } from '../hooks/useSettings';
import Badge from '../components/ui/Badge';

export default function Settings() {
  const { data: llmData } = useLlmProviders();
  const { data: savedSettings, isLoading } = useSettings();
  const saveSettings = useSaveSettings();
  const uploadLogo = useUploadLogo();
  const testProvider = useTestProvider();
  const logoRef = useRef(null);

  const providers = llmData?.providers || [];
  const defaultProvider = llmData?.default || 'openai';

  const [settings, setSettings] = useState({});
  const [testResult, setTestResult] = useState(null);

  // Settings aus API laden
  useEffect(() => {
    if (savedSettings) setSettings(savedSettings);
  }, [savedSettings]);

  const update = (key, val) => setSettings((s) => ({ ...s, [key]: val }));

  const handleSave = async () => {
    await saveSettings.mutateAsync(settings);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const result = await uploadLogo.mutateAsync(file);
      update('logo_url', result.logo_url);
    } catch (err) {
      alert('Upload fehlgeschlagen: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleTest = async (name) => {
    setTestResult(null);
    try {
      const result = await testProvider.mutateAsync(name);
      setTestResult({ name, ...result });
    } catch {
      setTestResult({ name, available: false });
    }
  };

  if (isLoading) return <div className="flex items-center justify-center h-64 text-gray-400">Wird geladen…</div>;

  return (
    <div>
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-[23px] font-bold mb-0.5">Einstellungen</h1>
          <p className="text-gray-500 text-[13.5px]">Plattform, Branding und LLM-Konfiguration</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saveSettings.isPending}
          className="bg-teal text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90 disabled:opacity-50"
        >
          {saveSettings.isPending ? 'Wird gespeichert…' : saveSettings.isSuccess ? 'Gespeichert!' : 'Speichern'}
        </button>
      </div>

      <div className="max-w-[640px] space-y-5">
        {/* Branding & Logo */}
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
          <h3 className="font-semibold text-[14.5px] mb-4">Branding & Logo</h3>
          <div className="space-y-4">
            <div className="flex gap-4 items-start">
              <div
                onClick={() => logoRef.current?.click()}
                className="w-[80px] h-[80px] rounded-xl border-2 border-dashed border-gray-200 hover:border-teal flex items-center justify-center cursor-pointer overflow-hidden shrink-0 transition-colors bg-sidebar"
              >
                {settings.logo_url ? (
                  <img src={settings.logo_url} alt="Logo" className="w-full h-full object-contain p-1.5" />
                ) : (
                  <span className="text-white/50 text-[10px] text-center leading-tight">Logo<br />hochladen</span>
                )}
              </div>
              <input ref={logoRef} type="file" className="hidden" accept="image/*" onChange={handleLogoUpload} />
              <div className="flex-1">
                <div className="mb-3">
                  <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Firmenname (Sidebar)</label>
                  <input type="text" value={settings.company_name || ''} onChange={(e) => update('company_name', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal" />
                </div>
                <div>
                  <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Untertitel</label>
                  <input type="text" value={settings.app_subtitle || ''} onChange={(e) => update('app_subtitle', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal" placeholder="z.B. CRO Platform" />
                </div>
              </div>
            </div>
            {settings.logo_url && (
              <button onClick={() => update('logo_url', '')} className="text-[12px] text-red-500 hover:text-red-700">Logo entfernen</button>
            )}
          </div>
        </div>

        {/* Sharing & PDF */}
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
          <h3 className="font-semibold text-[14.5px] mb-4">Sharing & PDF</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-1">
              <div>
                <div className="text-[13px] font-medium">Share-Links standardmäßig aktiv</div>
                <div className="text-[11.5px] text-gray-500">Neue Berichte sind sofort teilbar</div>
              </div>
              <button
                onClick={() => update('share_default_active', settings.share_default_active === '1' ? '0' : '1')}
                className={`w-10 h-[22px] rounded-full transition-colors relative ${settings.share_default_active === '1' ? 'bg-teal' : 'bg-gray-300'}`}
              >
                <div className={`w-[18px] h-[18px] bg-white rounded-full absolute top-[2px] transition-all ${settings.share_default_active === '1' ? 'left-[20px]' : 'left-[2px]'}`} />
              </button>
            </div>
            <div className="flex items-center justify-between py-1">
              <div>
                <div className="text-[13px] font-medium">Passwort-Schutz erforderlich</div>
                <div className="text-[11.5px] text-gray-500">Geteilte Berichte benötigen immer ein Passwort</div>
              </div>
              <button
                onClick={() => update('share_require_password', settings.share_require_password === '1' ? '0' : '1')}
                className={`w-10 h-[22px] rounded-full transition-colors relative ${settings.share_require_password === '1' ? 'bg-teal' : 'bg-gray-300'}`}
              >
                <div className={`w-[18px] h-[18px] bg-white rounded-full absolute top-[2px] transition-all ${settings.share_require_password === '1' ? 'left-[20px]' : 'left-[2px]'}`} />
              </button>
            </div>
            <div className="border-t border-gray-100 pt-4">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">PDF Primärfarbe</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={settings.pdf_primary_color || '#0C9488'} onChange={(e) => update('pdf_primary_color', e.target.value)} className="w-8 h-8 rounded-lg border border-gray-200 cursor-pointer p-0.5" />
                    <input type="text" value={settings.pdf_primary_color || ''} onChange={(e) => update('pdf_primary_color', e.target.value)} className="flex-1 border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal font-mono" />
                  </div>
                </div>
                <div>
                  <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">PDF Sekundärfarbe</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={settings.pdf_secondary_color || '#1C2E3C'} onChange={(e) => update('pdf_secondary_color', e.target.value)} className="w-8 h-8 rounded-lg border border-gray-200 cursor-pointer p-0.5" />
                    <input type="text" value={settings.pdf_secondary_color || ''} onChange={(e) => update('pdf_secondary_color', e.target.value)} className="flex-1 border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal font-mono" />
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">PDF Logo-URL</label>
                <input type="text" placeholder="Wird automatisch vom Sidebar-Logo übernommen" value={settings.pdf_logo_url || ''} onChange={(e) => update('pdf_logo_url', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal" />
                <p className="text-[11px] text-gray-400 mt-1">Leer lassen = Sidebar-Logo wird verwendet</p>
              </div>
              <div>
                <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">PDF Footer-Text</label>
                <input type="text" value={settings.pdf_footer_text || ''} onChange={(e) => update('pdf_footer_text', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal" />
              </div>
            </div>
          </div>
        </div>

        {/* Allgemein */}
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
          <h3 className="font-semibold text-[14.5px] mb-4">Allgemein</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Standard-Sprache</label>
              <select value={settings.default_language || 'de'} onChange={(e) => update('default_language', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal bg-white">
                <option value="de">Deutsch</option>
                <option value="en">English</option>
              </select>
            </div>
            <div>
              <label className="text-[12px] text-gray-500 block mb-1.5 font-medium">Standard-Seitentyp</label>
              <select value={settings.default_page_type || 'homepage'} onChange={(e) => update('default_page_type', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-[13px] outline-none focus:border-teal bg-white">
                <option value="homepage">Homepage</option>
                <option value="product">Produktseite</option>
                <option value="category">Kategorieseite</option>
                <option value="checkout">Checkout</option>
                <option value="landingpage">Landingpage</option>
              </select>
            </div>
          </div>
        </div>

        {/* LLM Provider */}
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
          <h3 className="font-semibold text-[14.5px] mb-4">LLM Provider</h3>
          <div className="space-y-3">
            {[
              { key: 'claude', icon: '🟣', label: 'Claude (Anthropic)', desc: 'Großes Kontextfenster (200k+)' },
              { key: 'openai', icon: '🟢', label: 'OpenAI', desc: 'GPT-4o · Nativer Vector Store' },
              { key: 'selfhosted', icon: '🔵', label: 'Self-Hosted', desc: 'Ollama / vLLM · DSGVO-konform' },
            ].map(({ key, icon, label, desc }) => {
              const p = providers.find(p => p.name === key);
              return (
                <div key={key} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[15px]">{icon}</span>
                      <span className="text-[14px] font-semibold">{label}</span>
                      {defaultProvider === key && <Badge bg="#E6F4F2" color="#0C9488">Standard</Badge>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge bg={p?.available ? '#D1FAE5' : '#FEE2E2'} color={p?.available ? '#10B981' : '#EF4444'}>
                        {p?.available ? 'Verfügbar' : 'Nicht konfiguriert'}
                      </Badge>
                      <button onClick={() => handleTest(key)} className="border border-gray-200 rounded-lg px-3 py-1.5 text-[12px] hover:bg-gray-50">Testen</button>
                    </div>
                  </div>
                  <div className="text-[12px] text-gray-500">
                    Modell: {p?.model || '—'} · {desc}
                  </div>
                  {testResult?.name === key && (
                    <div className={`mt-2 text-[12px] ${testResult.available ? 'text-green-600' : 'text-red-500'}`}>
                      {testResult.available ? 'Verbindung erfolgreich' : 'Verbindung fehlgeschlagen — API-Key prüfen'}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <p className="text-[11.5px] text-gray-400 mt-3">Standard-Provider wird in der .env konfiguriert (LLM_DEFAULT_PROVIDER). Provider kann pro Analyse überschrieben werden.</p>
        </div>
      </div>
    </div>
  );
}
